import React from 'react';
import { Button, Typography, Box, Tooltip } from '@quillbot/design-system';
import { makeStyles } from '@mui/styles';
import { extensionLinks } from 'config/constants';
import Track from 'src/authentication/handlers/track';
import { getQueryParams } from '@Common/handlers/helper';
import Link from 'next/link';
import QuillLocalStorage from 'core/utils/localStorageClient';
import { PlanInterface } from 'src/payments/models/paymentFlow';

const useStyles = makeStyles((theme) => ({
  successPaymentHoolder: {
    position: 'fixed',
    top: '0px',
    left: '0px',
    width: '100%',
    maxWidth: '100%',
    height: '100%',
    background: (props: { isIE: boolean }) =>
      props.isIE
        ? `#fff url("/images/theme/${theme.palette.mode}/IEsuccessPopper.jpg")`
        : `#fff url("/images/theme/${theme.palette.mode}/successPopperGif.svg")`,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    color: ' #fff',
    zIndex: 9,
    backgroundRepeat: 'no-repeat',
    backgroundSize: (props: { isIE: boolean }) => (props.isIE ? 'cover' : 'cover'),
    backgroundPosition: (props: { isIE: boolean }) =>
      props.isIE ? 'center center' : 'center center',
    '& h2': {
      fontSize: '58px',
      lineHeight: 'normal',
      margin: '0px auto',
      fontWeight: 'normal',
      color: theme.palette.primary.main,
      [theme.breakpoints.down('md')]: {
        fontSize: '32px',
      },
    },
  },

  successIllustration: {
    height: '309px',
    width: '258px',
    [theme.breakpoints.down('md')]: {
      height: '294px',
      width: '246px',
    },
  },

  successPaymentBtn: {
    borderRadius: '30px',
    fontSize: '14px',
    fontWeight: 700,
    lineHeight: '20px',
    minWidth: '202px',
    textAlign: 'center',
    minHeight: '36px',
  },
  addToChromeBtn: {
    padding: '6px 16px',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    color: theme.palette.primary.main,
  },
  extTooltip: {
    fontSize: 12,
    padding: 6,
    maxWidth: 140,
    textAlign: 'center',
  },

  btnContainer: {
    border: `1px solid ${theme.palette.primary.main}`,
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.primary.main,
    borderRadius: '100px',
    '&:hover': {
      backgroundColor: `${theme.palette.primary.main}0a`,
    },
  },
}));

export default function SuccessPaymentPage({
  show,
  planDetails,
}: {
  show: boolean;
  planDetails: PlanInterface;
}) {
  const isIE = true;
  const classes = useStyles({ isIE });

  const { title, price, saving, duration } = planDetails || {};
  const fromPathName = QuillLocalStorage.getInstance().get('upgradeVisitedFrom') || '/';
  const couponApplied = JSON.parse(QuillLocalStorage.getInstance().get(`couponcode`, '{}'));
  let finalAmount = price;
  if (couponApplied && couponApplied.discount) {
    finalAmount = Number((price - couponApplied.discount / 100).toFixed(2));
  }

  const pluralize = (value: number, text: string, suffix = 's') =>
    +value === 1 ? `${value} ${text}` : `${value} ${text}${suffix}`;

  function getProductName() {
    switch (fromPathName) {
      case '/grammar-check':
        return 'Grammar Checker';
      case '/plagiarism-checker':
        return 'Plagiarism Checker';
      case '/co-writer':
        return 'Co-Writer';
      case '/co-writer/home':
        return 'Co-Writer';
      case '/summarize':
        return 'Summarizer';
      case '/citation-generator':
        return 'Citation Generator';
      default:
        return 'Paraphraser';
    }
  }

  const onClickAddToChromeBtn = () => {
    Track.extensionLinkClicked('chrome', 'paymentSuccessPage');
    window.open(
      `${extensionLinks.chrome}${getQueryParams('paymentSuccessPage')}&utm_content=banner_01`,
      '_blank',
      'left=120,top=150,height=480,width=1200'
    );
  };

  const productsUrl = [
    '/',
    '/grammar-check',
    '/plagiarism-checker',
    '/co-writer',
    '/co-writer/home',
    '/summarize',
    '/citation-generator',
  ];

  return show ? (
    <Box
      minHeight="calc(100vh - 52px)"
      display={'flex'}
      alignItems="center"
      justifyContent="center"
    >
      <Box py={{ md: '52px', xs: '36px' }} px="12px">
        <Box maxWidth={568} alignItems="flex-start" margin="auto" display="flex">
          <Typography
            variant="h5"
            fontSize={{ md: '24px', sm: '20px', xs: '20px' }}
            color="primary.main"
            fontWeight={700}
            textAlign="center"
            sx={{ width: '100%' }}
            lineHeight={{ md: '34px', sm: '24px', xs: '24px' }}
            px={{ md: 0, sm: '4px', xs: '4px' }}
          >
            <span style={{ verticalAlign: 'middle' }}>
              <img
                height={25}
                width={25}
                src="./images/payments/successWithTick.svg"
                alt="tick icon"
              />
            </span>
            &nbsp; Thank you! We have received your payment. Groove on!
          </Typography>
        </Box>
        <Box pt={{ md: '44px', xs: '52px' }} />
        <Box
          margin="auto"
          maxWidth={756}
          display="flex"
          flexDirection={{ sm: 'column', xs: 'column', md: 'row' }}
          flexGrow={1}
        >
          <Box textAlign={'center'}>
            <img
              alt="success-illustration"
              className={classes.successIllustration}
              src="./images/paymentSuccessIllustration.svg"
            />
          </Box>
          {planDetails && title && price && duration && (
            <>
              <Box pr="86px" />
              <Box>
                <Box borderRadius={'6px'} px="18px" py="12px" mt="12px" bgcolor="secondary.lighter">
                  <Typography
                    fontSize="20px"
                    fontWeight={700}
                    lineHeight="27px"
                    color="text.primary"
                  >
                    Your Plan
                  </Typography>
                  <Box pt="8px" />
                  <Typography
                    fontSize="15px"
                    fontWeight={400}
                    lineHeight="20px"
                    color="text.primary"
                  >
                    Subscription: <b>{title}</b>
                  </Typography>
                  <Box pt="8px" />
                  <Typography
                    fontSize="15px"
                    fontWeight={400}
                    lineHeight="20px"
                    color="text.primary"
                  >
                    Paid: <b>${finalAmount} </b>
                    {couponApplied && couponApplied.coupon ? (
                      <Box component="span" pl="4px">
                        <Typography
                          component="span"
                          fontSize="12px"
                          fontStyle="italic"
                          fontWeight={600}
                          color="text.primary"
                          lineHeight={'16px'}
                        >
                          (Coupon applied)
                        </Typography>
                      </Box>
                    ) : (
                      saving &&
                      saving > 0 && (
                        <Box component="span" pl="4px">
                          <Typography
                            component="span"
                            fontSize="12px"
                            fontWeight={600}
                            color="primary.main"
                            lineHeight={'16px'}
                          >
                            Saved {saving}%
                          </Typography>
                        </Box>
                      )
                    )}
                  </Typography>
                  <Box pt="8px" />
                  {couponApplied && couponApplied.discount > 0 && (
                    <>
                      {' '}
                      <Typography
                        fontSize="15px"
                        fontWeight={400}
                        lineHeight="20px"
                        color="text.primary"
                      >
                        Discount: <b>- ${(couponApplied.discount / 100).toFixed(2)}</b>
                      </Typography>
                      <Box pt="8px" />
                    </>
                  )}

                  <Typography
                    fontSize="15px"
                    fontWeight={400}
                    lineHeight="20px"
                    color="text.primary"
                  >
                    Your subscription auto-renews every <b> {pluralize(duration, 'month')}.</b>
                  </Typography>
                </Box>
                {/* {showAddToChromeBtn && (
                  
                )} */}
                <Box display={{ xs: 'none', sm: 'none', md: 'block' }}>
                  <Box pt="32px" />
                  <Typography
                    textAlign={{ xs: 'center', sm: 'center', md: 'left' }}
                    fontSize="20px"
                    fontWeight={700}
                    color="text.primary"
                  >
                    QuillBot for Chrome
                  </Typography>
                  <Box pt="8px" />
                  <Typography
                    textAlign={{ xs: 'center', sm: 'center', md: 'left' }}
                    fontSize="15px"
                    fontWeight={400}
                    lineHeight="20px"
                    color="text.primary"
                  >
                    Install the QuillBot Chrome Extension and write like a pro every time.
                  </Typography>
                  <Box pt="16px" />
                  <Box maxWidth={227}>
                    <Box className={classes.btnContainer}>
                      <Tooltip
                        arrow
                        title="Take QuillBot everywhere you write"
                        placement="bottom"
                        classes={{
                          tooltip: classes.extTooltip,
                        }}
                      >
                        <Button onClick={onClickAddToChromeBtn} className={classes.addToChromeBtn}>
                          <img
                            loading="lazy"
                            src="/images/theme/commonImages/chrome.png"
                            alt="chrome icon"
                            height="22px"
                            width="22px"
                            style={{ marginRight: '7px' }}
                          />
                          <Typography
                            fontWeight={700}
                            fontSize="14px"
                            lineHeight="19px"
                            color="primary.main"
                          >
                            Add to Chrome. It’s free!
                          </Typography>
                        </Button>
                      </Tooltip>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </>
          )}
        </Box>
        <Box pt="52px" />
        <Box
          textAlign={{ xs: 'center', sm: 'center', md: 'left' }}
          display="flex"
          justifyContent="center"
        >
          <Link href={productsUrl.includes(fromPathName) ? (fromPathName as string) : '/'}>
            <Button
              variant="contained"
              className={classes.successPaymentBtn}
              color="primary"
              style={{}}
            >
              Return to {getProductName()}
            </Button>
          </Link>
        </Box>
      </Box>
    </Box>
  ) : (
    <></>
  );
}
